import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import {
    getFirestore,
    collection,
    doc,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    query,
    writeBatch,
    Timestamp
} from 'firebase/firestore';

// Components
import RestockHistory from './components/RestockHistory';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import InventoryList from './components/InventoryList';
import PointOfSale from './components/POS';
import SalesHistory from './components/SalesHistory';
import Reports from './components/Reports';
import Login from './components/Login';
import BulkSalesEntry from './components/BulkSalesEntry';
import { LogoutConfirmation, Loader } from './components/CommonComponents'

// --- Firebase Configuration ---
import firebaseConfig from './configs/firebase';
import Profile from './components/Profile';

// const appId = firebaseConfig.appId; // Use appId for artifact path
const appId = firebaseConfig.appId;


// --- Protected Route Component ---
function ProtectedRoute({ children }) {
    const { isAuthenticated, isAuthReady } = React.useContext(AppContext);

    if (!isAuthReady) {
        return <div className="flex justify-center items-center h-screen"><Loader /></div>;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
}

// --- App Context ---
const AppContext = React.createContext();

// --- Main App Component ---
export default function App() {
    const [db, setDb] = useState(null);
    // eslint-disable-next-line
    const [auth, setAuth] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const isRegisteringRef = React.useRef(false);

    const [inventory, setInventory] = useState([]);
    const [sales, setSales] = useState([]);
    const [restockHistory, setRestockHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [shopName, setShopName] = useState('StoreFlow');

    // --- Firebase Initialization and Auth ---
    useEffect(() => {
        const app = initializeApp(firebaseConfig);
        const firestoreDb = getFirestore(app);
        const firestoreAuth = getAuth(app);
        setDb(firestoreDb);
        setAuth(firestoreAuth);

        const unsubscribe = onAuthStateChanged(firestoreAuth, async (user) => {
            if (user) {
                setUserId(user.uid);
                // Skip setting authenticated if we're in the middle of registration
                // The register handler will set it after profile creation
                if (!isRegisteringRef.current) {
                    setIsAuthenticated(true);
                }
                console.log("User authenticated:", user.uid);

                // Fetch user profile to get shop name
                try {
                    const userProfileRef = doc(firestoreDb, `artifacts/${appId}/users/${user.uid}/profile/info`);
                    const userProfileSnap = await getDoc(userProfileRef);
                    if (userProfileSnap.exists()) {
                        const profileData = userProfileSnap.data();
                        setShopName(profileData.shopName || 'StoreFlow');
                        // Update document title
                        document.title = profileData.shopName || 'StoreFlow';
                    }
                } catch (error) {
                    console.error("Error fetching user profile:", error);
                }
            } else {
                try {
                    // await signInAnonymously(firestoreAuth);
                    setUserId(null);
                    setIsAuthenticated(false);
                    setShopName('StoreFlow');
                    document.title = 'StoreFlow';
                } catch (error) {
                    console.error("Authentication error:", error);
                }
            }
            setIsAuthReady(true);
        });

        return () => unsubscribe();
    }, []);

    // --- Data Fetching (Inventory & Sales) ---
    useEffect(() => {
        if (!isAuthReady || !db || !userId) return; // Only fetch if authenticated

        setLoading(true);
        const inventoryPath = `artifacts/${appId}/users/${userId}/inventory`;
        const salesPath = `artifacts/${appId}/users/${userId}/sales`;
        const restockPath = `artifacts/${appId}/users/${userId}/restockHistory`;

        const inventoryQuery = query(collection(db, inventoryPath));
        const salesQuery = query(collection(db, salesPath));
        const restockQuery = query(collection(db, restockPath));

        const unsubscribeInventory = onSnapshot(inventoryQuery, (snapshot) => {
            const inventoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setInventory(inventoryData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching inventory:", error);
            setLoading(false);
        });

        const unsubscribeSales = onSnapshot(salesQuery, (snapshot) => {
            const salesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            salesData.sort((a, b) => b.saleDate.seconds - a.saleDate.seconds);
            setSales(salesData);
        }, (error) => {
            console.error("Error fetching sales:", error);
        });

        const unsubscribeRestock = onSnapshot(restockQuery, (snapshot) => {
            const restockData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            restockData.sort((a, b) => b.restockDate.seconds - a.restockDate.seconds);
            setRestockHistory(restockData);
        }, (error) => {
            console.error("Error fetching restock history:", error);
        });

        return () => {
            unsubscribeInventory();
            unsubscribeSales();
            unsubscribeRestock();
        };
    }, [isAuthReady, db, userId]);

    // --- New Auth Handlers ---

    const handleLogin = async (email, password) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            setIsAuthenticated(true);
        } catch (error) {
            console.error("Login failed:", error.message);
            throw error; // Propagate error to the UI for feedback
        }
    };

    const handleRegister = async (email, password, newShopName) => {
        try {
            // Set flag to prevent onAuthStateChanged from navigating before profile is created
            isRegisteringRef.current = true;

            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Create user profile document with shop name
            // This must complete before the app navigates away
            const userProfileRef = doc(db, `artifacts/${appId}/users/${user.uid}/profile/info`);
            await setDoc(userProfileRef, {
                email: user.email,
                shopName: newShopName,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });

            // Update local state
            setShopName(newShopName);
            document.title = newShopName;

            // Now allow navigation
            isRegisteringRef.current = false;
            setIsAuthenticated(true);
        } catch (error) {
            isRegisteringRef.current = false;
            console.error("Registration failed:", error.message);
            throw error;
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            // Optionally, clear local state on logout
            setInventory([]);
            setSales([]);
            setRestockHistory([]);
        } catch (error) {
            console.error("Logout failed:", error.message);
        }
    };

    const handlePasswordReset = async (email) => {
        if (!auth) {
            console.error("Auth not initialized");
            return;
        }
        try {
            await sendPasswordResetEmail(auth, email);
            console.log("Password reset email sent to:", email);
        } catch (error) {
            console.error("Password reset failed:", error.message);
            throw error; // Propagate error to the UI for feedback
        }
    };

    const handleShopNameUpdate = async (newShopName) => {
        try {
            if (!userId) throw new Error('User not authenticated');

            const userProfileRef = doc(db, `artifacts/${appId}/users/${userId}/profile/info`);

            try {
                // Try to update the document first
                await updateDoc(userProfileRef, {
                    shopName: newShopName,
                    updatedAt: Timestamp.now()
                });
            } catch (updateError) {
                // If update fails because document doesn't exist, create it
                if (updateError.code === 'not-found') {
                    console.log("Profile document doesn't exist, creating it...");
                    await setDoc(userProfileRef, {
                        email: auth.currentUser?.email || '',
                        shopName: newShopName,
                        createdAt: Timestamp.now(),
                        updatedAt: Timestamp.now()
                    });
                } else {
                    // Re-throw other errors
                    throw updateError;
                }
            }

            // Update local state
            setShopName(newShopName);
            document.title = newShopName;
        } catch (error) {
            console.error("Shop name update failed:", error.message);
            throw error;
        }
    };

    // --- CRUD Operations ---
    const handleAddItem = async (item) => {
        if (!db || !userId) return;
        const inventoryPath = `artifacts/${appId}/users/${userId}/inventory`;
        await addDoc(collection(db, inventoryPath), {
            ...item,
            purchasePrice: parseFloat(item.purchasePrice),
            sellingPrice: parseFloat(item.sellingPrice),
            quantity: parseInt(item.quantity, 10),
            lastUpdated: Timestamp.now(),
        });
    };

    const handleUpdateItem = async (itemId, item) => {
        if (!db || !userId) return;
        const itemPath = `artifacts/${appId}/users/${userId}/inventory/${itemId}`;
        await setDoc(doc(db, itemPath), {
            ...item,
            purchasePrice: parseFloat(item.purchasePrice),
            sellingPrice: parseFloat(item.sellingPrice),
            quantity: parseInt(item.quantity, 10),
            lastUpdated: Timestamp.now(),
        }, { merge: true });
    };

    const handleRestockItem = async (itemId, newTotalQuantity) => {
        if (!db || !userId) return;

        const itemRef = doc(db, `artifacts/${appId}/users/${userId}/inventory/${itemId}`);
        const itemSnapshot = await getDoc(itemRef);

        if (itemSnapshot.exists()) {
            const currentItem = itemSnapshot.data();
            const quantityAdded = newTotalQuantity - (currentItem.quantity || 0);
            const restockPath = `artifacts/${appId}/users/${userId}/restockHistory`;
            const batch = writeBatch(db);

            // 1. Update inventory
            batch.update(itemRef, {
                quantity: newTotalQuantity,
                lastUpdated: Timestamp.now(),
            });

            // 2. Add restock record
            batch.set(doc(collection(db, restockPath)), {
                itemId: itemId,
                itemName: currentItem.itemName,
                quantityAdded: quantityAdded,
                newQuantity: newTotalQuantity,
                restockDate: Timestamp.now(),
            });

            await batch.commit();
        }
    };

    const handleDeleteItem = async (itemId) => {
        if (!db || !userId) return;
        const itemPath = `artifacts/${appId}/users/${userId}/inventory/${itemId}`;
        await deleteDoc(doc(db, itemPath));
    };

    const handleFinalizeSale = async (cart, customerForm, paymentMethod = 'cash') => {
        if (!db || !userId || cart.length === 0) return;

        const batch = writeBatch(db);
        const totalAmount = cart.reduce((sum, item) => sum + item.sellingPrice * item.quantity, 0);

        // 1. Add sale record with purchase price for profit calculation
        const salesPath = `artifacts/${appId}/users/${userId}/sales`;
        const saleRecord = {
            customer: {
                name: customerForm.name,
                phone: customerForm.phone
            },
            items: cart.map(cartItem => {
                const inventoryItem = inventory.find(invItem => invItem.id === cartItem.id);
                return {
                    itemId: cartItem.id,
                    itemName: cartItem.itemName,
                    sellingPrice: cartItem.sellingPrice,
                    purchasePrice: inventoryItem?.purchasePrice || 0,
                    quantity: cartItem.quantity
                };
            }),
            totalAmount,
            paymentMethod,
            saleDate: Timestamp.now(),
        };
        const salesRef = doc(collection(db, salesPath));
        batch.set(salesRef, saleRecord);

        // 2. Update inventory quantities
        cart.forEach(cartItem => {
            const inventoryItem = inventory.find(invItem => invItem.id === cartItem.id);
            if (inventoryItem) {
                const itemRef = doc(db, `artifacts/${appId}/users/${userId}/inventory/${cartItem.id}`);
                const newQuantity = inventoryItem.quantity - cartItem.quantity;
                batch.update(itemRef, { quantity: newQuantity });
            }
        });

        await batch.commit();
    };

    const handleFinalizeBulkSales = async (bulkSaleData) => {
        if (!db || !userId || bulkSaleData.entries.length === 0) return;

        const batch = writeBatch(db);
        const salesPath = `artifacts/${appId}/users/${userId}/sales`;

        // Create a single sale record for the bulk entry
        const saleRecord = {
            customer: {
                name: 'Bulk Entry',
                phone: ''
            },
            items: bulkSaleData.entries,
            totalAmount: bulkSaleData.totalAmount,
            saleDate: Timestamp.fromDate(new Date(bulkSaleData.date + 'T12:00:00')), // Set to noon of selected date
            isBulkEntry: true,
            bulkEntryDate: Timestamp.now() // When the bulk entry was created
        };

        const salesRef = doc(collection(db, salesPath));
        batch.set(salesRef, saleRecord);

        // Update inventory quantities
        bulkSaleData.entries.forEach(entry => {
            const inventoryItem = inventory.find(invItem => invItem.id === entry.itemId);
            if (inventoryItem) {
                const itemRef = doc(db, `artifacts/${appId}/users/${userId}/inventory/${entry.itemId}`);
                const newQuantity = inventoryItem.quantity - entry.quantity;
                batch.update(itemRef, { quantity: newQuantity });
            }
        });

        await batch.commit();
    };

    const handleDeleteSale = async (saleId) => {
        if (!db || !userId) return;

        // Find the sale to get item details for restoring inventory
        const sale = sales.find(s => s.id === saleId);
        if (!sale) return;

        const batch = writeBatch(db);

        // 1. Delete the sale record
        const salePath = `artifacts/${appId}/users/${userId}/sales/${saleId}`;
        batch.delete(doc(db, salePath));

        // 2. Restore inventory quantities
        sale.items.forEach(saleItem => {
            const inventoryItem = inventory.find(invItem => invItem.id === saleItem.itemId);
            if (inventoryItem) {
                const itemRef = doc(db, `artifacts/${appId}/users/${userId}/inventory/${saleItem.itemId}`);
                const restoredQuantity = inventoryItem.quantity + saleItem.quantity;
                batch.update(itemRef, { quantity: restoredQuantity });
            }
        });

        await batch.commit();
    };

    // Context value to share with all components
    const contextValue = {
        isAuthenticated,
        isAuthReady,
        auth,
        inventory,
        sales,
        restockHistory,
        loading,
        shopName,
        handleAddItem,
        handleUpdateItem,
        handleDeleteItem,
        handleRestockItem,
        handleFinalizeSale,
        handleFinalizeBulkSales,
        handleDeleteSale,
        handleLogout,
        handlePasswordReset,
        handleShopNameUpdate
    };

    return (
        <AppContext.Provider value={contextValue}>
            <Router>
                <Routes>
                    {/* Login Route */}
                    <Route
                        path="/login"
                        element={
                            isAuthReady && !isAuthenticated ? (
                                <Login onLogin={handleLogin} onRegister={handleRegister} onForgotPassword={handlePasswordReset} />
                            ) : isAuthReady && isAuthenticated ? (
                                <Navigate to="/" replace />
                            ) : (
                                <div className="flex justify-center items-center h-screen"><Loader /></div>
                            )
                        }
                    />

                    {/* Protected Routes */}
                    <Route path="/*" element={
                        <ProtectedRoute>
                            <AppLayout />
                        </ProtectedRoute>
                    } />
                </Routes>
            </Router>
        </AppContext.Provider>
    );
}

// --- App Layout Component (with Sidebar and Routes) ---
function AppLayout() {
    const { loading, auth, inventory, sales, restockHistory, shopName, handleAddItem, handleUpdateItem, handleDeleteItem, handleRestockItem, handleFinalizeSale, handleFinalizeBulkSales, handleDeleteSale, handleLogout, handlePasswordReset, handleShopNameUpdate } = React.useContext(AppContext);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    if (loading) {
        return <div className="flex justify-center items-center h-screen"><Loader /></div>;
    }

    const handleLogoutClick = () => {
        setShowLogoutModal(true);
    };

    const handleLogoutConfirm = async () => {
        await handleLogout();
        setShowLogoutModal(false);
    };

    const handleLogoutCancel = () => {
        setShowLogoutModal(false);
    };

    const toggleMobileSidebar = () => {
        setIsMobileSidebarOpen(!isMobileSidebarOpen);
    };

    return (
        <div className="flex h-screen bg-gray-100 font-sans">
            <Sidebar
                onLogout={handleLogoutClick}
                isMobileOpen={isMobileSidebarOpen}
                onMobileToggle={toggleMobileSidebar}
                shopName={shopName}
            />
            <main className="flex-1 p-2 sm:p-4 lg:p-6 overflow-y-auto pt-16 md:pt-2 md:ml-0">
                <div className="max-w-7xl mx-auto">
                    <Routes>
                        <Route path="/" element={<Dashboard user={auth?.currentUser} inventory={inventory} sales={sales} shopName={shopName} />} />
                        <Route path="/dashboard" element={<Navigate to="/" replace />} />
                        <Route path="/inventory" element={
                            <InventoryList
                                inventory={inventory}
                                onAdd={handleAddItem}
                                onUpdate={handleUpdateItem}
                                onDelete={handleDeleteItem}
                                onRestock={handleRestockItem}
                            />
                        } />
                        <Route path="/pos" element={<PointOfSale inventory={inventory} onFinalizeSale={handleFinalizeSale} shopName={shopName} />} />
                        <Route path="/bulk-sales" element={<BulkSalesEntry inventory={inventory} onFinalizeBulkSales={handleFinalizeBulkSales} />} />
                        <Route path="/history" element={<SalesHistory sales={sales} shopName={shopName} onDeleteSale={handleDeleteSale} />} />
                        <Route path="/restock-history" element={<RestockHistory restockHistory={restockHistory} />} />
                        <Route path="/reports" element={<Reports sales={sales} inventory={inventory} shopName={shopName} />} />
                        <Route path="/profile" element={<Profile auth={auth} onPasswordReset={handlePasswordReset} shopName={shopName} onShopNameUpdate={handleShopNameUpdate} />} />
                    </Routes>
                </div>
            </main>

            {/* Logout Confirmation Modal */}
            {showLogoutModal && (
                <LogoutConfirmation
                    onConfirm={handleLogoutConfirm}
                    onCancel={handleLogoutCancel}
                />
            )}
        </div>
    );
}